import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import dynamic from 'next/dynamic';
import styles from './product-authentication-infos.module.scss';

const ProductAuthenticationModal = dynamic(
  () => import('./product-authentication-modal/product-authentication-modal'),
  {
    ssr: false,
  },
);

type ProductAuthInfoProps = {
  inStock: boolean;
};

const ProductAuthInfo: React.FC<ProductAuthInfoProps> = ({ inStock }) => {
  const { t } = useTranslation();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  const variant = inStock ? 'express' : 'included';

  const onInfoClick = (): void => {
    // TODO(data): sendEvent('checkout_learn_more', { property: 'authenticated_shipping' })
    openModal(
      (modalDialogTitleId) => (
        <ProductAuthenticationModal
          modalDialogTitleId={modalDialogTitleId}
          variant={variant}
        />
      ),
      {
        styles: {
          content: {
            maxWidth: '41.25rem',
            width: '100%',
          },
        },
      },
    );
  };

  if (inStock) {
    return (
      <div
        className={clsx(styles.authenticationOption, styles['authenticationOption--express'])}
        data-cy="pdp_mandatory_physical_authentication"
      >
        <div className={styles.authenticationOption__header}>
          <span className={styles.authenticationOption__title}>
            <Icon
              name="express-delivery-default"
              className={styles.authenticationOption__title__icon}
              ariaHidden
            />
            {t('AUTHENTICATION_OPTION.EXPRESS_DELIVERY.TITLE', 'Express Delivery')}
          </span>
          <button
            type="button"
            className={styles.authenticationOption__info}
            data-cy="pdp_authentication_info_btn"
            aria-label={t(
              'AUTHENTICATION_OPTION.EXPRESS_DELIVERY.INFO_LABEL',
              'More information about express delivery',
            )}
            onClick={onInfoClick}
          >
            <Icon
              name="infobox-default"
              className={styles.authenticationOption__info__icon}
              ariaHidden
            />
          </button>
        </div>
        <ul className={styles.authenticationOption__checklist}>
          <li className={styles.authenticationOption__checklist__item}>
            <Icon
              name="tick-small-default"
              className={styles.authenticationOption__checklist__item__icon}
              ariaHidden
            />
            {t('AUTHENTICATION_OPTION.EXPRESS_DELIVERY.NEXT_DAY', 'Next-day shipping')}
          </li>
          <li className={styles.authenticationOption__checklist__item}>
            <Icon
              name="tick-small-default"
              className={styles.authenticationOption__checklist__item__icon}
              ariaHidden
            />
            {t('AUTHENTICATION_OPTION.EXPRESS_DELIVERY.AUTH_INCLUDED', 'Authentication & Quality Control included')}
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.authenticationOption, styles['authenticationOption--active'])}
      data-cy="pdp_mandatory_physical_authentication"
    >
      <div className={styles.authenticationOption__header}>
        <span className={styles.authenticationOption__title}>
          <Icon
            name="authentication-default"
            className={styles.authenticationOption__title__icon}
            ariaHidden
          />
          {t('AUTHENTICATION_OPTION.TITLE', 'Authentication & Quality Control')}
        </span>
        <button
          type="button"
          className={styles.authenticationOption__info}
          data-cy="pdp_authentication_info_btn"
          aria-label={t('AUTHENTICATION_OPTION.INFO_LABEL', 'More information about authentication')}
          onClick={onInfoClick}
        >
          <Icon
            name="infobox-default"
            className={styles.authenticationOption__info__icon}
            ariaHidden
          />
        </button>
      </div>
      <p className={styles.authenticationOption__description}>
        {t('AUTHENTICATION_OPTION.DESCRIPTION', 'Physical inspection by our experts before delivery.')}
      </p>
      <div className={styles.authenticationOption__footer}>
        <span className={styles.authenticationOption__tag}>{t('AUTHENTICATION_OPTION.INCLUDED', 'Included')}</span>
      </div>
    </div>
  );
};

export default React.memo(ProductAuthInfo);
